import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Clock3 } from "lucide-react";
import Reveal, { Eyebrow } from "@/components/Reveal";
import api from "@/lib/api";

export default function Collection() {
  const [products, setProducts] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let alive = true;
    api
      .get("/products")
      .then((res) => {
        if (!alive) return;
        setProducts(res.data.filter((p) => p.status === "coming_soon"));
        setLoaded(true);
      })
      .catch(() => setLoaded(true));
    return () => {
      alive = false;
    };
  }, []);

  return (
    <main data-testid="page-collection" className="pt-32 sm:pt-40 lg:pt-44">
      <section className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="max-w-3xl">
          <Reveal>
            <Eyebrow tone="blush">The collection</Eyebrow>
          </Reveal>
          <Reveal delay={0.05}>
            <h1 className="mt-7 font-display text-4xl font-semibold tracking-tight text-ink sm:text-5xl lg:text-6xl" data-testid="collection-headline">
              More corners of real life, on the way.
            </h1>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-6 max-w-2xl text-base leading-relaxed text-ink-soft sm:text-lg" data-testid="collection-sub">
              These editions are in the works. Nothing here is for sale yet—we'll never pretend otherwise. When a
              product launches, it will appear in the shop.
            </p>
          </Reveal>
        </div>

        <div className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {products.map((p, i) => (
            <Reveal key={p.slug} delay={(i % 3) * 0.06} className="h-full">
              <article
                data-testid={`collection-card-${p.slug}`}
                className="group flex h-full flex-col rounded-3xl border border-ink/5 bg-white p-8 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl hover:shadow-lav-soft/70"
              >
                <div className="flex items-start justify-between">
                  <span className="font-display text-3xl font-semibold text-lav">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-blush-soft px-3.5 py-1.5 text-[11px] font-bold uppercase tracking-[0.14em] text-blush-deep">
                    <Clock3 className="h-3 w-3" /> Coming soon
                  </span>
                </div>
                {p.cover_image && (
                  <div className="mt-5 overflow-hidden rounded-2xl shadow-md shadow-lav-soft/60">
                    <img
                      src={p.cover_image}
                      alt={`${p.name} cover`}
                      className="aspect-[2/3] w-full object-cover"
                      loading="lazy"
                    />
                  </div>
                )}
                <h2 className="mt-6 font-display text-xl font-semibold text-ink">{p.name}</h2>
                <p className="mt-1.5 font-display text-sm italic text-lav-deep">{p.tagline}</p>
                <p className="mt-3 flex-1 text-sm leading-relaxed text-ink-soft">{p.description}</p>
              </article>
            </Reveal>
          ))}
        </div>

        {loaded && products.length === 0 && (
          <p className="mt-14 text-center text-ink-soft" data-testid="collection-empty">
            The collection is still being written. Check back soon.
          </p>
        )}

        <Reveal>
          <div className="my-20 flex flex-col items-center justify-between gap-6 rounded-[2.5rem] bg-lav-soft px-6 py-12 text-center sm:px-12 lg:flex-row lg:text-left">
            <div>
              <h2 className="font-display text-2xl font-semibold text-lav-deep sm:text-3xl" data-testid="collection-notify-title">
                Want to know when something launches?
              </h2>
              <p className="mt-2 max-w-xl text-sm leading-relaxed text-ink-soft sm:text-base">
                Send us a note and we'll keep you posted. No spam, no pressure—just a quiet hello when the next
                journal is ready.
              </p>
            </div>
            <Link
              to="/contact"
              data-testid="collection-notify-link"
              className="group inline-flex shrink-0 items-center gap-2 rounded-full bg-ink px-8 py-4 font-bold text-cream transition-all duration-300 hover:bg-lav-deep"
            >
              Tell me when it's ready
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </div>
        </Reveal>
      </section>
    </main>
  );
}
